import { Fragment } from "react";
import cn from "classnames";
import { FoldChevron, NAV_ICONS } from "./nav.icons";
import type { PaneKey, VisibleNavGroup } from "./nav.model";
import { visibleSubs } from "./nav.model";

type NavGroupsProps = {
  groups: VisibleNavGroup[];
  pane: PaneKey;
  subTab: Record<PaneKey, string | null>;
  folded: Record<string, boolean>;
  onToggleFold: (key: string) => void;
  onSelectPane: (key: PaneKey) => void;
  onSelectSub: (key: PaneKey, sub: string) => void;
};

/**
 * The sidebar itself: one `.pc-nav` block per group, as the design draws it.
 * The same markup serves the wide layout and the drawer — only the CSS around
 * it differs — so this takes no position on where it is rendered.
 */
export const NavGroups = ({
  groups,
  pane,
  subTab,
  folded,
  onToggleFold,
  onSelectPane,
  onSelectSub,
}: NavGroupsProps) => (
  <>
    {groups.map((group) => {
      const isFolded = folded[group.key] === true;

      return (
        <nav
          key={group.key}
          className={cn("pc-nav", group.className, { "is-fold": isFolded })}
          aria-label={group.title}
        >
          <div
            className="pc-nav__t"
            role="button"
            tabIndex={0}
            aria-expanded={!isFolded}
            onClick={() => onToggleFold(group.key)}
            onKeyDown={(event) => {
              if (event.key !== "Enter" && event.key !== " ") return;

              event.preventDefault();
              onToggleFold(group.key);
            }}
          >
            {group.title}
            <FoldChevron />
          </div>

          {group.items.map((item) => {
            const isOpen = pane === item.key;
            const subs = visibleSubs(item);

            return (
              <Fragment key={item.key}>
                <button
                  type="button"
                  data-pane={item.key}
                  className={cn({ "is-on": isOpen })}
                  aria-current={isOpen ? "page" : undefined}
                  onClick={() => onSelectPane(item.key)}
                >
                  <i>{NAV_ICONS[item.key]}</i>
                  {item.label}
                </button>

                {isOpen && item.tabsInPane !== true && subs.length > 0 && (
                  <div className="pc-sub" data-sub={item.key}>
                    {subs.map((sub) => (
                      <button
                        key={sub.key}
                        type="button"
                        className={cn("pc-sub__b", { "is-on": subTab[item.key] === sub.key })}
                        onClick={() => onSelectSub(item.key, sub.key)}
                      >
                        {sub.label}
                      </button>
                    ))}
                  </div>
                )}
              </Fragment>
            );
          })}
        </nav>
      );
    })}
  </>
);
